import { useState, useEffect, useMemo, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCloudArrowDown } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router";
import { useTable } from "react-table";

import PageTemplate from "../Templates/PageTemplate";
import exportOptions from "./Components/exportOptions";
import TablePreset from "../Table/TablePreset";
import middleware from "../Api/Middleware";
import SortableHeader from "../Table/TableComponents/SortableHeader";
import ToastError from "../Toasts/ToastError";
import CheckboxGrid from "./Components/checkboxGrid";
import { BackButton, ButtonBlue } from "../HouseStyle/Components/Buttons";
import { BorderedDropDown } from "../HouseStyle/Components/DropDowns";
import { BorderedTextInput } from "../HouseStyle/Components/Inputs";
import * as Icons from "../HouseStyle/Icons";

import "./ImportExport.css";

function ExportPage() {
    const [tableLoading, setTableLoading] = useState(false);
    const [selectedTable, setSelectedTable] = useState(""); // Selected head table
    const [selectedGroupTables, setSelectedGroupTables] = useState([]); // Group checkboxes
    const [fileName, setFileName] = useState(""); // Description for the file
    const [loading, setLoading] = useState(false);
    const [fileNameError, setFileNameError] = useState(false);
    const [tableData, setTableData] = useState([]); // Data of the selected table
    const [pagination, setPagination] = useState({
        page_size: 10,
        page: 1,
        max_items: 0,
        begin_of_page: 0,
        end_of_page: 0,
    });
    const [order] = useState({ orderBy: "", direction: "" });
    const navigate = useNavigate();

    // Helper to extract useful error information from axios/middleware errors
    const getErrorDetails = (error) => {
        const status = error?.response?.status ?? error?.status ?? null;
        const data = error?.response?.data ?? error?.data ?? null;
        const message = (data && (data.message || data.error)) || error?.message || (typeof data === 'string' ? data : JSON.stringify(data)) || 'Onbekende fout';
        return { status, message, data };
    };

    // Fetch data for the currently selected main table or group
    const fetchTableData = useCallback(async () => {
        setTableLoading(true);
        try {
            if (!exportOptions.exportTypes[selectedTable]) {
                setTableLoading(false);
                return;
            }

            // Create an array of all selected tables (main table + selected groups)
            const withKeys = selectedGroupTables.reduce((acc, groupKey) => {
                return acc.concat(exportOptions.groupOptions[exportOptions.exportTypes[selectedTable].displayName][groupKey].withKeys);
            }, []);

            const modelMapping = selectedGroupTables.reduce((acc, groupKey) => {
                return {
                    ...acc,
                    ...exportOptions.groupOptions[exportOptions.exportTypes[selectedTable].displayName][groupKey].modelMapping
                };
            }, {});
            
            const requestPayload = {
                main_model: exportOptions.exportTypes[selectedTable].model,
                foreign_key: exportOptions.exportTypes[selectedTable].foreignKey,
            };
            
            // Convert requestPayload and additional parameters to a query string, excluding models from URLSearchParams
            const queryParams = new URLSearchParams({
                main_model: requestPayload.main_model,
                foreign_key: requestPayload.foreign_key,
                page: pagination.page,
                page_size: pagination.page_size,
                ...(order.orderBy && order.direction ? { orderBy: order.orderBy, direction: order.direction } : {})
            }).toString();
            
            // Append models as an array in the request body instead
            const tableDataResults = await middleware.get(`/export?${queryParams}`, {
                params: { models: requestPayload.models, withkeys: withKeys || [], modelMapping: modelMapping || []}
            });
            


            // Set table data (aggregated from all selected tables)
            // const aggregatedData = tableDataResults.map((result) => result.data || []);
            setTableData(tableDataResults.data.data);
    
            // Update pagination based on the primary table’s data (use functional updater to avoid stale dependency)
            setPagination(prev => ({
                ...prev,
                max_items: tableDataResults.data.pagination.total_records,
                begin_of_page: (tableDataResults.data.pagination.page - 1) * parseInt(tableDataResults.data.pagination.page_size),
                end_of_page: tableDataResults.data.pagination.page * parseInt(tableDataResults.data.pagination.page_size),
            }));
        } catch (error) {
            setTableData([]);
            const { status, message, data } = getErrorDetails(error);
            ToastError(`Fout bij ophalen van tabeldata (status: ${status ?? 'onbekend'}): ${message}`);
            console.error("Error fetching data:", { status, message, data, error });
        } finally {
            setTableLoading(false);
        }
     }, [selectedTable, pagination.page, pagination.page_size, order.orderBy, order.direction, selectedGroupTables]);

    // Start export for selected main table and any checked groups
    const StartExport = async () => {
        if (!selectedTable) return;
        if (!fileName) {
            setFileNameError(true);
            return;
        }
        setFileNameError(false);
        try {
            setLoading(true);
            if (!exportOptions.exportTypes[selectedTable]) {
                setLoading(false);
                return;
            }

            // Create an array of all selected tables (main table + selected groups)
            const withKeys = selectedGroupTables.reduce((acc, groupKey) => {
                return acc.concat(exportOptions.groupOptions[exportOptions.exportTypes[selectedTable].displayName][groupKey].withKeys);
            }, []);

            console.log(selectedTable, selectedGroupTables);
            

            const groupOptionsDisplayNames = selectedGroupTables
            .reduce((acc, groupKey) => {
                return acc.concat(exportOptions.groupOptions[exportOptions.exportTypes[selectedTable].displayName][groupKey].displayName);
            }, [])
            .join(', ');
            console.log(groupOptionsDisplayNames);
            
            const requestPayload = {
                selected_group_options_label: groupOptionsDisplayNames || "",
                main_model: exportOptions.exportTypes[selectedTable].model,
                foreign_key: exportOptions.exportTypes[selectedTable].foreignKey,
                withkeys: withKeys,
                description: fileName,
                table: selectedTable
            };
            
            const csvDataResult = await middleware.post(`/export`, requestPayload);

            navigate(`/export/resultaat/${csvDataResult.data.export_id}`);
        } catch (error) {
            const { status, message, data } = getErrorDetails(error);
            ToastError(`Fout bij export (status: ${status ?? 'onbekend'}): ${message}`);
            console.error("Export error:", { status, message, data, error });
        } finally {
            setLoading(false);
        }
    };

    // Clear and fetch table data when changing the main table
    useEffect(() => {
        setSelectedGroupTables([]);
        fetchTableData();
    }, [selectedTable]);

    useEffect(() => {
        fetchTableData();
    }, [pagination.page_size, pagination.page, order, order.direction, selectedGroupTables]);

    // Table columns
    const columns = useMemo(() => {
        if (!tableData || tableData.length === 0) return [];
        // return [];
        // Gather all unique keys across all rows, including indexed fields
        const allKeys = new Set();
        tableData.forEach(row => {
            Object.keys(row).forEach(key => allKeys.add(key));
        });
    
        return Array.from(allKeys).map((key) => ({
            Header: key,
            accessor: key,
            Cell: ({ row }) =>
                key === "product_description" ? (
                    <div dangerouslySetInnerHTML={{ __html: row.original[key] && row.original[key].length > 40
                        ? `${row.original[key].slice(0, 40)}...`
                        : row.original[key] }} />
                ) : (
                    <p>{row.original[key] && row.original[key].length > 40
                        ? `${row.original[key].slice(0, 40)}...`
                        : row.original[key]}</p>
                ),
        }));
    }, [tableData]);

    const tableInstance = useTable({ columns, data: tableData });

    // Handle checkbox selection for grouped tables
    const handleCheckboxChange = (key) => {
        if (key === "all") {
            // If "all" is selected, either select all group options or clear all
            setSelectedGroupTables((prev) => 
                prev.length === Object.keys(exportOptions.groupOptions[exportOptions.exportTypes[selectedTable].displayName]).length
                    ? [] // Unselect all if all are selected
                    : Object.keys(exportOptions.groupOptions[exportOptions.exportTypes[selectedTable].displayName]) // Select all
            );
        } else {
            // Toggle individual group options
            setSelectedGroupTables((prev) =>
                prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key]
            );
        }
    };

    const isAllSelected = 
    selectedTable &&
    selectedGroupTables &&
    exportOptions.exportTypes[selectedTable] &&
    exportOptions.groupOptions[exportOptions.exportTypes[selectedTable].displayName] &&
    selectedGroupTables.length === Object.keys(exportOptions.groupOptions[exportOptions.exportTypes[selectedTable].displayName]).length;

    const exportTypeOptions = Object.entries(exportOptions.exportTypes).map(([key, value]) => ({
        value: key,
        title: value.displayName
    }));

    return (
        <PageTemplate pageIsLoading={loading} navbarTitle={"Nieuwe Export"}>
            <div className="import-page import-layout">
              <div className="d-flex justify-content-start mb-4">
                <div className="d-flex gap-3 align-items-center">
                  <BackButton text={`Terug`} onClickFunction={() => navigate('/import-export-center#imports')} />
                </div>
              </div>

              <div className="import-card">
                    <form onSubmit={(e) => { e.preventDefault(); StartExport(); }} className="import-form">
                        <section className="mb-3">
                            <h2>Exporteer type</h2>
                            <BorderedDropDown
                                placeholder="-- Selecteer een export type --"
                                options={exportTypeOptions}
                                selectedValue={selectedTable}
                                setSelectedValue={setSelectedTable}
                                size="regular"
                            />
                        </section>

                        {selectedTable && exportOptions.groupOptions[exportOptions.exportTypes[selectedTable].displayName] && (
                            <section className="mb-3">
                                <h2>Groepopties</h2>
                                <div className="group-options-container p-3" style={{ backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                                    <div className="form-check mb-3">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id="checkbox-all"
                                            checked={isAllSelected}
                                            onChange={() => handleCheckboxChange("all")}
                                        />
                                        <label className="form-check-label fw-bold" htmlFor="checkbox-all">Selecteer alles</label>
                                    </div>

                                    <div style={{ borderTop: '1px solid #dee2e6', paddingTop: '1rem' }}>
                                        <CheckboxGrid
                                            exportOptions={exportOptions}
                                            selectedTable={selectedTable}
                                            selectedGroupTables={selectedGroupTables}
                                            handleCheckboxChange={handleCheckboxChange}
                                        />
                                    </div>
                                </div>
                            </section>
                        )}

                        <section className="mb-3">
                            <h2>Beschrijving</h2>
                            <BorderedTextInput
                                inputValue={fileName}
                                setInputValue={setFileName}
                                placeholder="Bestand beschrijving"
                                size="regular"
                                error={fileNameError}
                                errorTooltip="Beschrijving is verplicht"
                            />
                        </section>

                        <button
                            type="submit"
                            className="btn btn-primary submit-btn"
                            disabled={!(selectedTable && fileName)}
                        >
                            Exporteer
                        </button>
                    </form>

                    {selectedTable && (
                        <section className="import-preview-card mt-4">
                            <div className="import-preview-card__title mb-3">
                                <h2 className="mb-1">Preview gegevens</h2>
                                <p className="import-preview-card__subtitle">
                                     Bekijk de preview van de export.
                                </p>
                            </div>

                            <div className="import-preview-card__table import-export-table">
                                {tableLoading && tableData.length === 0 ? (
                                   null
                                ) : tableData.length > 0 ? (
                                    <TablePreset
                                        data={[
                                            {
                                                tableInstance: tableInstance,
                                                pagination: pagination,
                                                primary_key: "consumer_id",
                                            },
                                        ]}
                                        tableLoading={tableLoading}
                                        setPagination={setPagination}
                                        handleSelection={() => {}}
                                    />
                                ) : (
                                    <p className="import-preview-card__empty">
                                        Nog geen resultaten voor deze selectie. Pas je filters aan of kies een andere groep.
                                    </p>
                                )}
                            </div>
                        </section>
                    )}
                </div>
            </div>
        </PageTemplate>
    );
}

export default ExportPage;